/**
 * PrintStage - Final stage of animation workflow
 * Displays completed videos with print (finalize) and download options
 * Takes are ordered chronologically regardless of mode
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from "@/components/ui/button";
import { 
  Download, Film, Trash2, Upload, Loader2, 
  ChevronLeft, ChevronRight, Star, CheckCircle2, Printer, ArrowLeft
} from "lucide-react";
import { API, getAssetUrl } from "@/utils/studioUtils";
import api from "@/config/axiosInstance";

export function PrintStage({
  sceneId,
  projectId,
  animationTakes,
  setAnimationTakes,
  lipsyncTakes,
  setLipsyncTakes,
  activeTakeIndex,
  setActiveTakeIndex,
  uploadedSceneVideo,
  setUploadedSceneVideo,
  setStrategyChoice,
  setLipsyncUnlockedForUpload,
  setCurrentStage,
  uploadingVideo,
  setUploadingVideo,
  printedTakeId,
  setPrintedTakeId,
  scene
}) {
  const navigate = useNavigate();
  const [downloadingTake, setDownloadingTake] = useState(null);
  const [printingTake, setPrintingTake] = useState(false);

  // Navigate to project editor with playhead at this shot
  const handleReturnToEditor = () => {
    // Get the shot's start position (order * some default duration, or use actual timeline data)
    const shotOrder = scene?.order || 0;
    navigate(`/project/${projectId}/editor?shot=${sceneId}&order=${shotOrder}`);
  };

  // Build unified takes array and sort by created_at chronologically
  const allTakes = [
    ...animationTakes.map((take, idx) => ({ 
      ...take, 
      type: take.mode === 'dialogue' ? 'dialogue' : take.mode === 'upload' ? 'upload' : 'action',
      originalIndex: idx,
      source: 'animation'
    })),
    ...lipsyncTakes.map((take, idx) => ({ 
      ...take, 
      type: 'lipsync',
      originalIndex: idx,
      source: 'lipsync'
    }))
  ].sort((a, b) => {
    // Sort by created_at timestamp (oldest first = Take 1)
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateA - dateB;
  });

  // On initial load, default to printed take or most recent
  useEffect(() => {
    if (allTakes.length > 0) {
      // Find the printed take
      const printedIndex = allTakes.findIndex(t => t.id === printedTakeId);
      
      if (printedIndex !== -1) {
        // Default to printed take
        setActiveTakeIndex(printedIndex);
      } else {
        // Default to most recent take (last in chronological order)
        setActiveTakeIndex(allTakes.length - 1);
      }
    }
  }, [printedTakeId]); // Only run when printedTakeId changes (initial load)

  const safeIndex = Math.min(activeTakeIndex, Math.max(0, allTakes.length - 1));
  const activeTake = allTakes[safeIndex];

  // Handle video upload
  const handleVideoUpload = async (file) => {
    if (!file) return;
    try {
      setUploadingVideo(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "video");
      formData.append("name", file.name || "uploaded_video");
      toast.info("Uploading video...");
      
      const response = await api.post(`/projects/${projectId}/assets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const uploadedTake = {
        id: `upload-${Date.now()}`,
        video_url: response.data.file_url,
        thumbnail_url: null,
        prompt: "",
        mode: "upload",
        created_at: new Date().toISOString(),
        status: "completed"
      };
      
      const newTakes = [...animationTakes, uploadedTake];
      setAnimationTakes(newTakes);
      
      // Save to backend
      await api.put(`/scenes/${sceneId}`, {
        animation_takes: newTakes
      });
      
      // Set active index to the new take (will be last after sorting)
      const newAllTakes = [...allTakes, { ...uploadedTake, type: 'upload', source: 'animation' }];
      setActiveTakeIndex(newAllTakes.length - 1);
      
      toast.success("Video uploaded and added to takes!");
    } catch (err) {
      toast.error("Failed to upload video");
      console.error(err);
    } finally {
      setUploadingVideo(false);
    }
  };

  // Handle print (mark take for final output)
  const handlePrint = async (take) => {
    if (!take?.id) return;
    
    setPrintingTake(true);
    try {
      await api.put(`/scenes/${sceneId}`, {
        printed_take_id: take.id
      });
      setPrintedTakeId(take.id);
      toast.success(`Take #${safeIndex + 1} marked for final output!`);
    } catch (err) {
      toast.error('Failed to mark take');
      console.error(err);
    } finally {
      setPrintingTake(false);
    }
  };

  // Handle download
  const handleDownload = async (take, index) => {
    if (!take?.video_url) return;
    
    setDownloadingTake(index);
    try {
      const videoUrl = getAssetUrl(take.video_url);
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scene_${sceneId}_take_${index + 1}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download video');
      console.error(err);
    } finally {
      setDownloadingTake(null);
    }
  };

  // Handle delete take
  const handleDeleteTake = async (take, index) => {
    try {
      if (take.source === 'lipsync') {
        await api.delete(`/scenes/${sceneId}/lipsync-takes/${take.id}`);
        setLipsyncTakes(prev => prev.filter(t => t.id !== take.id));
      } else {
        await api.delete(`/scenes/${sceneId}/animation-takes/${take.id}`);
        setAnimationTakes(prev => prev.filter(t => t.id !== take.id));
      }
      
      // Clear printed take if it was deleted
      if (take.id === printedTakeId) {
        setPrintedTakeId(null);
        await api.put(`/scenes/${sceneId}`, { printed_take_id: null });
      }
      
      if (activeTakeIndex >= index && activeTakeIndex > 0) {
        setActiveTakeIndex(prev => prev - 1);
      }
      
      toast.success('Take deleted');
    } catch (err) {
      toast.error('Failed to delete take');
      console.error(err);
    }
  };

  // Get type label
  const getTypeLabel = (type) => {
    switch (type) {
      case 'dialogue': return 'Dialogue';
      case 'lipsync': return 'Lip-Synced';
      case 'upload': return 'Uploaded';
      default: return 'Animation';
    }
  };

  // Get type color
  const getTypeColor = (type) => {
    switch (type) {
      case 'dialogue': return 'text-purple-400 bg-purple-500/20';
      case 'lipsync': return 'text-pink-400 bg-pink-500/20';
      case 'upload': return 'text-blue-400 bg-blue-500/20';
      default: return 'text-green-400 bg-green-500/20';
    }
  };

  const isPrinted = activeTake?.id === printedTakeId;

  return (
    <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4" data-testid="print-stage">
      {/* Main Video Area */}
      <div className="flex-1 flex flex-col items-center overflow-auto">
        {/* Video Display */}
        <div 
          className="relative rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950 mb-4 shrink-0"
          style={{ 
            aspectRatio: "16/9",
            width: '100%',
            maxWidth: 'min(900px, calc((100vh - 320px) * 16 / 9))',
            maxHeight: 'calc(100vh - 320px)'
          }}
        >
          {/* Handle strategy workflow uploadedSceneVideo */}
          {uploadedSceneVideo && allTakes.length === 0 ? (
            <video
              key={uploadedSceneVideo}
              src={getAssetUrl(uploadedSceneVideo)}
              controls
              className="w-full h-full object-contain"
            />
          ) : allTakes.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500">
              <div className="text-center">
                <Film className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No video available</p>
              </div>
            </div>
          ) : !activeTake?.video_url ? (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500">
              <div className="text-center">
                <Film className="w-12 h-12 mx-auto mb-2 opacity-50 animate-pulse" />
                <p>Loading video...</p>
              </div>
            </div>
          ) : (
            <video
              key={`print-video-${safeIndex}-${activeTake?.id || activeTake?.video_url}`}
              src={getAssetUrl(activeTake.video_url)}
              controls
              autoPlay
              loop
              className="w-full h-full object-contain"
            />
          )}
          
          {/* Printed badge overlay */}
          {isPrinted && (
            <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-emerald-600 text-white text-xs rounded-full">
              <CheckCircle2 className="w-3 h-3" />
              Printed
            </div>
          )}
        </div>

        {/* Upload video option */}
        {!uploadedSceneVideo && (
          <div className="w-full max-w-4xl mb-4 px-2">
            <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-zinc-700 rounded-lg hover:border-purple-500 hover:bg-purple-500/5 transition-colors cursor-pointer">
              <input
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => handleVideoUpload(e.target.files?.[0])}
                data-testid="print-upload-video-input"
              />
              {uploadingVideo ? (
                <>
                  <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                  <span className="text-sm text-purple-400">Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 text-zinc-400" />
                  <span className="text-sm text-zinc-400">Upload a video as a new take</span>
                </>
              )}
            </label>
          </div>
        )}

        {/* Uploaded video indicator (for strategy workflow) */}
        {uploadedSceneVideo && (
          <div className="w-full max-w-4xl mb-4 px-2 space-y-3">
            <div className="flex items-center justify-between p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Film className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-purple-300">Uploaded scene video</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setUploadedSceneVideo(null);
                  setStrategyChoice(null);
                  setLipsyncUnlockedForUpload(false);
                  setCurrentStage("strategy");
                }}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        )}

        {/* Takes Carousel */}
        {allTakes.length > 0 && (
          <div className="w-full max-w-4xl px-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-white">Takes ({allTakes.length})</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveTakeIndex(prev => Math.max(0, prev - 1))}
                disabled={activeTakeIndex === 0}
                className="shrink-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="flex-1 flex gap-2 overflow-x-auto py-2">
                {allTakes.map((take, index) => (
                  <div
                    key={take.id || index}
                    onClick={() => setActiveTakeIndex(index)}
                    className={`relative shrink-0 w-32 h-20 rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                      index === safeIndex 
                        ? 'border-purple-500 ring-2 ring-purple-500/30' 
                        : take.id === printedTakeId
                          ? 'border-emerald-500 ring-1 ring-emerald-500/30'
                          : 'border-zinc-700 hover:border-zinc-500'
                    }`}
                  >
                    {take.thumbnail_url ? (
                      <img 
                        src={getAssetUrl(take.thumbnail_url)} 
                        alt={`Take ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                        <Film className="w-6 h-6 text-zinc-600" />
                      </div>
                    )}
                    
                    {/* Type badge */}
                    <span className={`absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded ${getTypeColor(take.type)}`}>
                      {getTypeLabel(take.type)}
                    </span>
                    
                    {/* Take number */}
                    <span className="absolute bottom-1 right-1 text-[10px] text-white bg-black/60 px-1.5 py-0.5 rounded">
                      #{index + 1}
                    </span>
                    
                    {/* Printed indicator */}
                    {take.id === printedTakeId && (
                      <span className="absolute top-1 right-1">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      </span>
                    )}
                  </div>
                ))}
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveTakeIndex(prev => Math.min(allTakes.length - 1, prev + 1))}
                disabled={activeTakeIndex >= allTakes.length - 1}
                className="shrink-0"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Right Panel - Actions */}
      {activeTake?.video_url && (
        <div className="w-full lg:w-72 shrink-0 bg-zinc-900/50 rounded-lg border border-zinc-800 p-4 space-y-4">
          {/* Current Take Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded ${getTypeColor(activeTake.type)}`}>
                {getTypeLabel(activeTake.type)}
              </span>
              <span className="text-sm font-medium text-white">
                Take #{safeIndex + 1}
              </span>
              {isPrinted && (
                <span className="flex items-center gap-1 text-xs text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  Printed
                </span>
              )}
            </div>
          </div>

          {/* Print Button */}
          <div className="space-y-2">
            <Button
              onClick={() => handlePrint(activeTake)}
              disabled={printingTake || isPrinted}
              className={`w-full gap-2 ${
                isPrinted 
                  ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600/30' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
              data-testid="print-take-button"
            >
              {printingTake ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isPrinted ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <Printer className="w-4 h-4" />
              )}
              {isPrinted ? 'Currently Printed' : 'Print This Take'}
            </Button>
            <p className="text-xs text-zinc-500 leading-relaxed">
              Printing marks this take as the final version for the editing room. 
              You can change this selection at any time.
            </p>
          </div>

          {/* Download Button */}
          <div className="space-y-2 pt-2 border-t border-zinc-800">
            <Button
              variant="outline"
              onClick={() => handleDownload(activeTake, safeIndex)}
              disabled={downloadingTake === safeIndex}
              className="w-full gap-2 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="download-take-button"
            >
              {downloadingTake === safeIndex ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download Video
            </Button>
          </div>

          {/* Delete Button */}
          <div className="pt-2 border-t border-zinc-800">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteTake(activeTake, safeIndex)}
              className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="delete-take-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Take
            </Button>
          </div>

          {/* Return to Project Editor */}
          <div className="pt-2 border-t border-zinc-800">
            <Button
              variant="outline"
              onClick={handleReturnToEditor}
              className="w-full gap-2 border-indigo-600/50 text-indigo-400 hover:text-white hover:bg-indigo-600/20"
              data-testid="return-to-editor-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Go to Project Editor
            </Button>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Go back to the timeline editor with this shot selected.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrintStage;
